import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Badge,
  Box,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import FolderSpecialOutlinedIcon from "@mui/icons-material/FolderSpecialOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import ViewCompactOutlinedIcon from "@mui/icons-material/ViewCompactOutlined";
import TextFormatIcon from "@mui/icons-material/TextFormat";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useConcepts, getDisplayState } from "../hooks/useConcepts";
import { ProjectSidebarNav } from "./ProjectSidebarNav";
import { projectRailId } from "./projectRail";
import { TeamSidebarNav } from "./TeamSidebarNav";
import { BuildingChip } from "./BuildingChip";
import { MattoAiTag } from "./MattoAiTag";
import { useWorkspaceStore, workspacesEnabled } from "ui";
import { useMattoState } from "../MattoState";
import { SideBarMode } from "../ts/app_interfaces";
import { isBoardEditorPath } from "../libs/boardUrls";
import { AuthContext } from "../auth/Auth";
import { useLoginState } from "../studioxr/auth/LoginState";
import { trackEvent } from "../libs/analyticsManager";
import {
  MY_LIBRARY_LABEL,
  MY_LIBRARY_ROUTE,
  REFERENCE_IMAGES_LABEL,
  REFERENCE_IMAGES_ROUTE,
  STUDIO_LIBRARY_LABEL,
  STUDIO_LIBRARY_ROUTE,
} from "./uploads/constants";
import { useStudioLibraryAvailable } from "./studio-library/useStudioLibraryAvailable";
import AutoAwesomeMosaicOutlinedIcon from "@mui/icons-material/AutoAwesomeMosaicOutlined";
import { SHOWROOMS_LABEL, SHOWROOMS_ROUTE } from "./showrooms/constants";
import {
  IMAGE_EDIT_CONCEPT_SOURCE,
  IMAGE_EDIT_LABEL,
  IMAGE_EDIT_ROUTE,
} from "./concepts_scheme/constants";
import { SHOWROOMS_ENABLED } from "./showrooms/publicAccess";
import { SketchupPromoCard } from "./library/SketchupPromoCard";
import {
  navIcons,
  navSubheaderSx,
  connectorSx,
  navRowSx,
  navRowIconSx,
  navRowTextProps,
} from "./navIcons";

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  targetTab: string;
  href: string;
  requiresTeam?: boolean;
  // nav2 mockup sections: "create" items nest under the CREATE label;
  // "library" items are the indented sub-headers under Library. Top-level
  // items have no section. Library sub-items are expanded-rail only.
  section?: "create" | "library";
  // Feature not built yet — clicking shows a "Coming Soon" snackbar instead
  // of navigating.
  comingSoon?: boolean;
  // Purple MattoAI tag right after the label (expanded rail only).
  aiTag?: boolean;
  // Shows the Building chip while this row's concepts are processing.
  building?: { noun: string; variant?: "editing" };
}

const NAV_ITEMS: NavItem[] = [
  // nav2 mockup order: Dashboard, Projects, Library (+ sub-headers), Team,
  // then the CREATE group of content types.
  {
    id: "dashboard",
    label: "Dashboard",
    icon: navIcons.dashboard,
    targetTab: "Dashboard",
    href: "/home/dashboard",
  },
  // Showrooms — a flat row below Dashboard, opening the brand directory.
  // Hidden entirely where showrooms are off (staging-only until launch).
  {
    id: "showrooms",
    label: SHOWROOMS_LABEL,
    icon: navIcons.showrooms,
    targetTab: "Showrooms",
    href: SHOWROOMS_ROUTE,
  },
  {
    id: "projects",
    label: "Projects",
    icon: navIcons.projects,
    targetTab: "Projects",
    href: "/home/projects",
  },
  {
    id: "team",
    label: "Team",
    icon: navIcons.team,
    targetTab: "Team",
    href: "/home/team",
    requiresTeam: true,
  },
  // Library group — expandable parent (2a). Only the Mattoboard library is
  // live, so it's the group's one sub-item until Studio/Images ship.
  {
    id: "library",
    label: "Library",
    icon: navIcons.library,
    targetTab: "Library",
    href: "",
    comingSoon: true,
  },
  {
    id: "mattoboard-library",
    label: "Mattoboard",
    icon: navIcons.matto,
    targetTab: "MattoboardLibrary",
    href: "/home/library",
    section: "library",
  },
  // The workspace's most-used products (studio_library flag).
  {
    id: "studio-library",
    label: STUDIO_LIBRARY_LABEL,
    icon: navIcons.studio,
    targetTab: "StudioLibrary",
    href: STUDIO_LIBRARY_ROUTE,
    section: "library",
  },
  // The #3621 "Images" slot, now the My Library page (the user's own uploads —
  // distinct from the Mattoboard catalog Library above).
  {
    id: "my-library",
    label: MY_LIBRARY_LABEL,
    icon: navIcons.myLibrary,
    targetTab: "MyLibrary",
    href: MY_LIBRARY_ROUTE,
    section: "library",
  },
  // The board editor's MY IMAGES as a page, listed right below My Library.
  {
    id: "reference-images",
    label: REFERENCE_IMAGES_LABEL,
    icon: navIcons.images,
    targetTab: "ReferenceImages",
    href: REFERENCE_IMAGES_ROUTE,
    section: "library",
  },
  // Schedules is deliberately NOT in the global CREATE group — per the DS2
  // mockup it's project-scoped only (project sidebar + project overview).
  {
    id: "boards",
    label: "Boards",
    icon: navIcons.boards,
    targetTab: "Boards",
    href: "/home/boards",
    section: "create",
  },
  // Upload-and-edit concepts only; its page offers just New Image Edit.
  {
    id: "image-edit",
    label: IMAGE_EDIT_LABEL,
    icon: navIcons.imageEdit,
    targetTab: "ImageEdit",
    href: IMAGE_EDIT_ROUTE,
    section: "create",
    aiTag: true,
    building: { noun: "image edit", variant: "editing" },
  },
  {
    id: "concepts",
    label: "Concepts",
    icon: navIcons.concepts,
    targetTab: "Concepts",
    href: "/home/concepts",
    section: "create",
    building: { noun: "concept" },
  },
  {
    id: "collections",
    label: "Collections",
    icon: navIcons.collections,
    targetTab: "Collections",
    href: "/home/collections",
    section: "create",
  },
];

// Editor-rail widths; the drawers in HomeLayout/StartingOptions import the
// exported ones so the sidebar and its drawer host can't desync.
const COLLAPSED_WIDTH = 60;
export const EXPANDED_WIDTH = 280;
// 2a nav (workspace + project modes): 230px grouped sidebar, 64px icon rail
// when collapsed. The editor rail keeps the classic widths above.
const WS_COLLAPSED_WIDTH = 64;
export const WS_EXPANDED_WIDTH = 230;

// aside container props shared by the editor-rail and 2a variants.
const asideBaseSx = {
  backgroundColor: "#fff",
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  height: "100%",
} as const;

const SIDEBAR_LIBRARY_BUTTONS = [
  {
    label: "Mattoboard Library",
    shortLabel: "Mattoboard Library",
    mode: SideBarMode.Library,
    icon: <StyleOutlinedIcon fontSize="small" />,
  },
  {
    label: MY_LIBRARY_LABEL,
    shortLabel: MY_LIBRARY_LABEL,
    mode: SideBarMode.YourMaterials,
    icon: <FolderSpecialOutlinedIcon fontSize="small" />,
  },
  {
    label: STUDIO_LIBRARY_LABEL,
    shortLabel: "Studio",
    mode: SideBarMode.StudioLibrary,
    icon: <AutoAwesomeMosaicOutlinedIcon fontSize="small" />,
  },
  {
    label: REFERENCE_IMAGES_LABEL,
    shortLabel: REFERENCE_IMAGES_LABEL,
    mode: SideBarMode.Uploads,
    icon: <ImageOutlinedIcon fontSize="small" />,
  },
  {
    label: "Browse Collections",
    shortLabel: "Collections",
    mode: SideBarMode.Collections,
    icon: <FolderOpenRoundedIcon fontSize="small" />,
  },
  {
    label: "Templates",
    shortLabel: "Templates",
    mode: SideBarMode.Templates,
    icon: <ViewCompactOutlinedIcon fontSize="small" />,
  },
  {
    label: "Labels",
    shortLabel: "Labels",
    mode: SideBarMode.Text,
    icon: <TextFormatIcon fontSize="small" />,
  },
  {
    label: "Matched Products",
    shortLabel: "Products",
    mode: SideBarMode.MatchedProducts,
    icon: <Inventory2OutlinedIcon fontSize="small" />,
  },
  {
    label: "Tray",
    shortLabel: "Tray",
    mode: SideBarMode.Tray,
    icon: <InboxOutlinedIcon fontSize="small" />,
  },
];

interface HomeSideBarProps {
  selectedTab: string;
  defaultCollapsed?: boolean;
  hideToggle?: boolean;
  isConcepts2Route?: boolean;
  showMatchedProductsSidebarButton?: boolean;
  /** Board editor only: show the Tray rail entry (persisted boards). */
  showTraySidebarButton?: boolean;
  disablePanelToggle?: boolean;
  /** Desktop home shell only: room for the bottom-left SketchUp promo card. */
  showSketchupCard?: boolean;
  onBeforeNavigate?: () => boolean;
  onNavigate?: () => void;
}

export const HomeSideBar = ({
  selectedTab,
  defaultCollapsed = false,
  hideToggle = false,
  isConcepts2Route = false,
  showMatchedProductsSidebarButton = false,
  showTraySidebarButton = false,
  disablePanelToggle = false,
  showSketchupCard = false,
  onBeforeNavigate,
  onNavigate,
}: HomeSideBarProps) => {
  const location = useLocation();
  const isProjectRoute = isBoardEditorPath(location.pathname);
  // Project-mode sidebar (#3544): inside a project the global nav is replaced
  // by ProjectSidebarNav.
  const activeScheduleProjectId = useMattoState(
    (state) => state.activeScheduleProjectId,
  );
  const projectSidebarId = projectRailId(
    location.pathname,
    location.search,
    activeScheduleProjectId,
  );
  const showTemplates = isProjectRoute;
  // "Labels" (text/line/logo overlays) only belongs in the 3D board editor
  // (/board/:id), not in the concept image editor (/home/concepts2/editor/:id).
  const showLabels = isProjectRoute;
  const isSmall = useMediaQuery("(max-width:900px)");
  const isDrawer = !!onNavigate;
  const [collapsed, setCollapsed] = useState(
    isDrawer ? false : isSmall || defaultCollapsed,
  );
  const [libraryOpen, setLibraryOpen] = useState(true);
  const navigate = useNavigate();
  const sideBarMode = useMattoState((state) => state.sideBarMode);
  const setSideBarMode = useMattoState((state) => state.setSideBarMode);
  const sideBarPanelOpen = useMattoState((state) => state.sideBarPanelOpen);
  const setSideBarPanelOpen = useMattoState(
    (state) => state.setSideBarPanelOpen,
  );
  const setSnackbarMessage = useMattoState((state) => state.setSnackbarMessage);
  const { currentUser } = React.useContext(AuthContext);
  const setShowLogin = useLoginState((state) => state.setShowLogin);
  // Show the Team nav entry only when the active workspace is a team. Team
  // membership alone (permissions.teamsAdmin/teamsDesigner) is not enough —
  // a user in Personal workspace has no team context to manage, so the
  // entry would dead-end.
  // Reads the store directly (bypasses useWorkspaceContext), so gate it on the
  // production hide-flag too — otherwise a stale persisted `team:` id would
  // still surface the Team nav entry. See featureFlag.ts.
  const activeWorkspaceIsTeamRaw = useWorkspaceStore((s) => {
    if (!currentUser?.uid) return false;
    const id = s.activeIdByUid[currentUser.uid];
    return typeof id === "string" && id.startsWith("team:");
  });
  const activeWorkspaceIsTeam = workspacesEnabled() && activeWorkspaceIsTeamRaw;
  const studioLibraryEnabled = useStudioLibraryAvailable();
  // Team drill-down (Team Page v3): the nav slides to TeamSidebarNav on any
  // /home/team page; Back slides home without leaving the page.
  const onTeamRoute = /^\/home\/team(\/|$)/.test(location.pathname);
  const [teamNavOpen, setTeamNavOpen] = useState(onTeamRoute);
  useEffect(() => setTeamNavOpen(onTeamRoute), [onTeamRoute]);
  const visibleNavItems = useMemo(
    () =>
      NAV_ITEMS.filter(
        (item) =>
          (!item.requiresTeam || activeWorkspaceIsTeam) &&
          (item.id !== "showrooms" || SHOWROOMS_ENABLED) &&
          (item.id !== "studio-library" || studioLibraryEnabled),
      ),
    [activeWorkspaceIsTeam, studioLibraryEnabled],
  );
  // nav2 mockup: the WORKSPACE group (Library parent last), its library
  // sub-items, then a CREATE section grouping the content types.
  const mainNavItems = visibleNavItems.filter((item) => !item.section);
  const libraryNavItems = visibleNavItems.filter(
    (item) => item.section === "library",
  );
  const createNavItems = visibleNavItems.filter(
    (item) => item.section === "create",
  );

  // Real-time concepts list drives the "building" indicator on the Concepts
  // nav item (#3544) — the hook is already loaded app-wide, no extra fetching.
  const { concepts } = useConcepts();
  // Processing count per nav row id: upload/edit concepts count on Image
  // Edit, the rest on Concepts.
  const processingByNavId = useMemo(() => {
    const counts: Record<string, number> = { concepts: 0, "image-edit": 0 };
    for (const c of concepts) {
      if (getDisplayState(c) !== "PENDING") continue;
      counts[
        c.source === IMAGE_EDIT_CONCEPT_SOURCE ? "image-edit" : "concepts"
      ]++;
    }
    return counts;
  }, [concepts]);

  useEffect(() => {
    if (!isDrawer) setCollapsed(isSmall || defaultCollapsed);
  }, [isSmall, defaultCollapsed, isDrawer]);

  // Auto-collapse the nav rail when on the image editor route.
  useEffect(() => {
    if (!isDrawer && location.pathname.startsWith("/home/concepts2/editor/")) {
      setCollapsed(true);
    }
  }, [location.pathname, isDrawer]);

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  // The editor rail (concept image editor + board editor) has no collapse
  // control at all (#3621) — it's a fixed rail like the board editor's, and
  // every button already shows its label, so there is nothing to expand into.
  // The workspace/project sidebars keep the edge handle below.

  // 2a edge handle — straddles the sidebar's right border, works in both
  // states. Replaces the toggle row in workspace mode (desktop only).
  const edgeHandle = !hideToggle && !isDrawer && (
    <Tooltip title={collapsed ? "Expand" : "Collapse"} placement="right">
      <IconButton
        onClick={toggleCollapsed}
        aria-label={collapsed ? "Open sidebar" : "Close sidebar"}
        data-testid="sidebar-toggle"
        sx={{
          position: "absolute",
          right: -11,
          top: "50%",
          transform: "translateY(-50%)",
          width: 22,
          height: 52,
          borderRadius: "11px",
          bgcolor: "#fff",
          border: "1px solid #E0DEDA",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          color: "#71717a",
          // The handle overhangs the border into the page, so it has to beat
          // page content — sticky headers and full-bleed sections live in the
          // 2..12 band and were painting straight over it. Below the app bar,
          // drawers and modals, which should still cover it.
          zIndex: (theme) => theme.zIndex.appBar - 1,
          "&:hover": { bgcolor: "#FAFAF9" },
          "& svg": {
            transition: "transform 200ms",
            transform: collapsed ? "rotate(180deg)" : "none",
          },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );

  const sideBarLibraryButtons = useMemo(
    () =>
      SIDEBAR_LIBRARY_BUTTONS.filter(
        (btn) =>
          (btn.mode !== SideBarMode.MatchedProducts ||
            showMatchedProductsSidebarButton) &&
          (btn.mode !== SideBarMode.Tray || showTraySidebarButton) &&
          (btn.mode !== SideBarMode.Templates || showTemplates) &&
          (btn.mode !== SideBarMode.Text || showLabels) &&
          (btn.mode !== SideBarMode.StudioLibrary || studioLibraryEnabled),
      ),
    [
      showMatchedProductsSidebarButton,
      showTraySidebarButton,
      showTemplates,
      showLabels,
      studioLibraryEnabled,
    ],
  );
  // The open panel can't stay on Studio Library once it's unavailable (a
  // Personal board was opened and the workspace followed it).
  useEffect(() => {
    if (sideBarMode === SideBarMode.StudioLibrary && !studioLibraryEnabled)
      setSideBarMode(SideBarMode.Library);
  }, [sideBarMode, studioLibraryEnabled, setSideBarMode]);

  const renderNavItem = (item: NavItem) => {
    const isActive = selectedTab === item.targetTab;
    const buildingCount = item.building ? processingByNavId[item.id] : 0;
    const showBuilding = buildingCount > 0;
    const buildingLabel = `${buildingCount} ${item.building?.noun}${
      buildingCount === 1 ? "" : "s"
    } processing`;
    const toggleGroup = () => setLibraryOpen((prev) => !prev);
    const handleClick = () => {
      // 2a: the Library parent toggles its sub-items instead of toasting.
      // The collapsed icon rail never renders those sub-items, so the toggle
      // would be a dead click — send the user straight to the Mattoboard
      // library instead (#3618).
      if (item.id === "library") {
        if (collapsed) {
          if (onBeforeNavigate && !onBeforeNavigate()) return;
          navigate("/home/library");
          onNavigate?.();
          return;
        }
        setLibraryOpen((prev) => !prev);
        return;
      }
      // Team drills into its sub-nav. In the drawer this keeps the drawer
      // open so the team pages stay one tap away.
      if (item.id === "team") {
        if (onTeamRoute) {
          setTeamNavOpen(true);
          return;
        }
        if (onBeforeNavigate && !onBeforeNavigate()) return;
        navigate("/home/team/members");
        return;
      }
      if (item.comingSoon) {
        setSnackbarMessage(`${item.label} — Coming Soon`);
        return;
      }
      if (item.id === "concepts") trackEvent("Side Bar - Concepts");
      if (item.id === "boards") trackEvent("Side Bar - Boards");
      if (item.id === "image-edit") trackEvent("Side Bar - Image Edit");
      if (item.id === "projects") trackEvent("Side Bar - Projects");
      if (onBeforeNavigate && !onBeforeNavigate()) return;
      navigate(item.href);
      onNavigate?.();
    };

    // Sectioned items (library subs + CREATE) render indented under the
    // connector line; library subs are slightly shorter.
    const isSubItem = item.section === "library";
    // The Library row toggles its sub-items on click; the collapsed rail
    // (which never shows them) navigates to the library instead.
    const isExpandable = item.id === "library";
    const groupOpen = libraryOpen;
    const button = (
      <ListItemButton
        key={item.id}
        disableRipple
        onClick={handleClick}
        selected={isActive}
        sx={{
          ...navRowSx({
            collapsed,
            indented: !!item.section,
            height: isSubItem ? 36 : 38,
          }),
          // Collapsed rail: stack a small text label under the icon so new
          // users aren't left guessing at bare glyphs (#3618).
          ...(collapsed && {
            flexDirection: "column",
            height: "auto",
            py: "6px",
            gap: "2px",
          }),
        }}
      >
        <ListItemIcon sx={navRowIconSx(isActive, collapsed)}>
          {showBuilding ? (
            <Badge color="secondary" variant="dot" overlap="circular">
              {item.icon}
            </Badge>
          ) : (
            item.icon
          )}
        </ListItemIcon>
        {collapsed ? (
          <Typography
            sx={{
              fontSize: "0.6rem",
              lineHeight: 1.1,
              fontWeight: 600,
              textAlign: "center",
              color: isActive ? "primary.main" : "text.secondary",
            }}
          >
            {item.label}
          </Typography>
        ) : (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={navRowTextProps}
            // The tag sits right after the label, not at the row's far edge.
            sx={item.aiTag ? { flex: "0 1 auto", mr: "8px" } : undefined}
          />
        )}
        {!collapsed && item.aiTag && !showBuilding && <MattoAiTag />}
        {!collapsed && showBuilding && (
          <BuildingChip
            label={buildingLabel}
            variant={item.building?.variant}
          />
        )}
        {!collapsed && isExpandable && (
          // Its own click target so it stays keyboard-reachable; the row
          // itself toggles the group too.
          <ExpandMoreIcon
            role="button"
            tabIndex={0}
            aria-label={
              groupOpen ? `Collapse ${item.label}` : `Expand ${item.label}`
            }
            aria-expanded={groupOpen}
            onClick={(e) => {
              e.stopPropagation();
              toggleGroup();
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              e.stopPropagation();
              toggleGroup();
            }}
            sx={{
              ml: "auto",
              fontSize: 16,
              color: "#a1a1aa",
              transition: "transform 150ms",
              transform: groupOpen ? "none" : "rotate(90deg)",
              cursor: "pointer",
              borderRadius: "4px",
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
              },
            }}
          />
        )}
        {!collapsed && item.id === "team" && (
          <ChevronRightIcon
            sx={{ ml: "auto", fontSize: 16, color: "#a1a1aa" }}
          />
        )}
      </ListItemButton>
    );
    return collapsed ? (
      <Tooltip key={item.id} title={item.label} placement="right">
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  // Editor-rail "Back to Dashboard" entry (#dashboard-nav). Lives at the top of
  // the concept/board editor rail so leaving for the dashboard is a discoverable
  // action instead of the unlabeled logo. Reuses the global-nav dashboard icon
  // and the same collapsed/expanded layout as the rail's panel buttons, but it
  // navigates (via the shared onBeforeNavigate dirty-guard) rather than toggling
  // a SideBarMode panel.
  const editorDashboardButton = (() => {
    const button = (
      <ListItemButton
        disableRipple
        onClick={() => {
          trackEvent("Side Bar - Dashboard");
          if (onBeforeNavigate && !onBeforeNavigate()) return;
          navigate("/home/dashboard");
          onNavigate?.();
        }}
        sx={{
          borderRadius: 3,
          flexDirection: collapsed ? "column" : "row",
          alignItems: "center",
          px: collapsed ? 0.5 : 2,
          py: collapsed ? 1 : undefined,
          justifyContent: collapsed ? "center" : "flex-start",
          "&:hover": collapsed
            ? { backgroundColor: "rgba(15,23,42,0.04)" }
            : undefined,
        }}
      >
        <ListItemIcon
          sx={{
            color: "text.secondary",
            minWidth: collapsed ? "auto" : 36,
            mr: collapsed ? 0 : 1,
            justifyContent: "center",
          }}
        >
          {navIcons.dashboard}
        </ListItemIcon>
        {collapsed ? (
          <Typography
            sx={{
              mt: 0.25,
              fontSize: "0.6rem",
              lineHeight: 1.1,
              fontWeight: 600,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            Dashboard
          </Typography>
        ) : (
          <ListItemText
            primary="Dashboard"
            primaryTypographyProps={{ sx: { fontWeight: 600 } }}
          />
        )}
      </ListItemButton>
    );
    return collapsed ? (
      <Tooltip title="Back to Dashboard" placement="right">
        {button}
      </Tooltip>
    ) : (
      button
    );
  })();

  // Workspace nav — home context only; the concept/board editor rail shows
  // just its panel buttons.
  const workspaceNav = (
    <Box sx={{ flexGrow: 1 }}>
      <List
        disablePadding
        subheader={
          !collapsed ? (
            <ListSubheader disableSticky sx={navSubheaderSx}>
              WORKSPACE
            </ListSubheader>
          ) : undefined
        }
      >
        {mainNavItems.map(renderNavItem)}
      </List>
      {/* Library sub-items — 2a: collapsible, with a connector line
          spanning just the rows. */}
      <Collapse in={libraryOpen && !collapsed} timeout={180} unmountOnExit>
        <Box sx={{ position: "relative" }}>
          <Box sx={connectorSx("19px")} />
          <List disablePadding>{libraryNavItems.map(renderNavItem)}</List>
        </Box>
      </Collapse>
      {collapsed ? (
        <>
          {/* 2a rail: divider stands in for the CREATE label */}
          <Divider sx={{ my: "8px", mx: "10px" }} />
          <List disablePadding>{createNavItems.map(renderNavItem)}</List>
        </>
      ) : (
        <List
          disablePadding
          sx={{ mt: "16px" }}
          subheader={
            <ListSubheader disableSticky sx={navSubheaderSx}>
              CREATE
            </ListSubheader>
          }
        >
          <Box sx={{ position: "relative" }}>
            <Box sx={connectorSx("9px")} />
            {createNavItems.map(renderNavItem)}
          </Box>
        </List>
      )}
    </Box>
  );

  return (
    <Box
      component="aside"
      sx={
        // Editor rail keeps the classic container; workspace AND project
        // modes share the 2a container (230px / 64px rail).
        isConcepts2Route
          ? {
              ...asideBaseSx,
              width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
              transition: "width 0.3s ease",
              borderRight: "1px solid rgba(15,23,42,0.08)",
            }
          : {
              ...asideBaseSx,
              position: "relative",
              width: isDrawer
                ? "100%"
                : collapsed
                  ? WS_COLLAPSED_WIDTH
                  : WS_EXPANDED_WIDTH,
              maxWidth: WS_EXPANDED_WIDTH,
              transition: "width 200ms cubic-bezier(.4,0,.2,1)",
              borderRight: "1px solid #EFEEEC",
              // Keep the edge handle visible where it straddles the border.
              overflow: "visible",
            }
      }
    >
      <Paper
        elevation={0}
        sx={{
          flexGrow: 1,
          borderRadius: 0,
          ...(isConcepts2Route
            ? { px: collapsed ? 1 : 1.5, py: 1.5 }
            : { px: "10px", pt: "12px", pb: 1.5 }),
          display: "flex",
          flexDirection: "column",
          gap: 2,
          backgroundColor: "transparent",
        }}
      >
        {/* Workspace + project modes collapse via the edge handle. The editor
            rail has no toggle row at all, so the Dashboard entry sits at the
            top of the rail. */}
        {isConcepts2Route && (
          <>
            <List disablePadding>
              {editorDashboardButton}
              <Divider sx={{ my: "16px", mx: collapsed ? "6px" : "10px" }} />
              {sideBarLibraryButtons.flatMap((btn) => {
                const isSelected = sideBarPanelOpen && sideBarMode === btn.mode;
                const button = (
                  <ListItemButton
                    key={btn.label}
                    disableRipple
                    selected={isSelected}
                    onClick={() => {
                      if (btn.mode === SideBarMode.Library)
                        trackEvent("Side Bar - Library");
                      if (btn.mode === SideBarMode.Uploads)
                        trackEvent("Side Bar - Images");
                      if (btn.mode === SideBarMode.YourMaterials)
                        trackEvent("Side Bar - My Library");
                      if (btn.mode === SideBarMode.StudioLibrary)
                        trackEvent("Side Bar - Studio Library");
                      if (
                        (btn.mode === SideBarMode.Collections ||
                          btn.mode === SideBarMode.YourMaterials ||
                          btn.mode === SideBarMode.StudioLibrary) &&
                        !currentUser?.uid
                      ) {
                        setShowLogin("signup");
                        return;
                      }
                      if (sideBarPanelOpen && sideBarMode === btn.mode) {
                        if (!disablePanelToggle) setSideBarPanelOpen(false);
                      } else {
                        setSideBarMode(btn.mode);
                        setSideBarPanelOpen(true);
                      }
                    }}
                    sx={{
                      borderRadius: 3,
                      flexDirection: collapsed ? "column" : "row",
                      alignItems: "center",
                      px: collapsed ? 0.5 : 2,
                      py: collapsed ? 1 : undefined,
                      justifyContent: collapsed ? "center" : "flex-start",
                      "&:hover": collapsed
                        ? { backgroundColor: "rgba(15,23,42,0.04)" }
                        : undefined,
                      "&.Mui-selected": {
                        backgroundColor: "rgba(99,102,241,0.12)",
                        "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
                          color: "primary.main",
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      // Anchor for the tile "send to Tray" fly animation
                      // (libs/flyToTray) — the ghost lands here, then the
                      // icon pulses.
                      {...(btn.mode === SideBarMode.Tray
                        ? { "data-tray-rail-icon": "" }
                        : {})}
                      sx={{
                        color: isSelected ? "primary.main" : "text.secondary",
                        minWidth: collapsed ? "auto" : 36,
                        mr: collapsed ? 0 : 1,
                        justifyContent: "center",
                      }}
                    >
                      {btn.icon}
                    </ListItemIcon>
                    {collapsed ? (
                      <Typography
                        sx={{
                          mt: 0.25,
                          fontSize: "0.6rem",
                          lineHeight: 1.1,
                          fontWeight: 600,
                          textAlign: "center",
                          color: isSelected ? "primary.main" : "text.secondary",
                        }}
                      >
                        {btn.shortLabel}
                      </Typography>
                    ) : (
                      <ListItemText
                        primary={btn.label}
                        primaryTypographyProps={{ sx: { fontWeight: 600 } }}
                      />
                    )}
                  </ListItemButton>
                );
                // Collections is the last item shared with the Home library
                // nav; Templates/Labels/Matched Products/Tray below it are
                // Creator-only tools, so the same divider style as the one
                // under Dashboard marks that boundary (#4022).
                return btn.mode === SideBarMode.Collections
                  ? [
                      button,
                      <Divider
                        key="library-tools-divider"
                        sx={{ my: "16px", mx: collapsed ? "6px" : "10px" }}
                      />,
                    ]
                  : [button];
              })}
            </List>
          </>
        )}

        {projectSidebarId ? (
          <ProjectSidebarNav
            projectId={projectSidebarId}
            collapsed={collapsed}
            onBeforeNavigate={onBeforeNavigate}
            onNavigate={onNavigate}
          />
        ) : isConcepts2Route ? null : activeWorkspaceIsTeam ? (
          <DrillSlider
            open={teamNavOpen}
            main={workspaceNav}
            sub={
              <TeamSidebarNav
                collapsed={collapsed}
                onBack={() => setTeamNavOpen(false)}
                onBeforeNavigate={onBeforeNavigate}
                onNavigate={onNavigate}
              />
            }
          />
        ) : (
          workspaceNav
        )}
        {/* Under both rails; a hand-collapsed 64px rail hides it. */}
        {showSketchupCard && !collapsed && <SketchupPromoCard />}
      </Paper>
      {!isConcepts2Route && edgeHandle}
    </Box>
  );
};

// The hidden panel turns invisible once the slide ends, so the collapsed
// rail's wide labels can't peek into the visible one.
const drillPanelSx = (shown: boolean) =>
  ({
    width: "50%",
    minWidth: 0,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    visibility: shown ? "visible" : "hidden",
    transition: shown ? "visibility 0s" : "visibility 0s linear 240ms",
  }) as const;

/** Two nav panels side by side; `open` slides the second one in. */
const DrillSlider = ({
  open,
  main,
  sub,
}: {
  open: boolean;
  main: React.ReactNode;
  sub: React.ReactNode;
}) => {
  const mainRef = useRef<HTMLDivElement>(null);
  const subRef = useRef<HTMLDivElement>(null);
  // React 18 has no `inert` prop; it keeps the off-screen panel out of the
  // tab order.
  useEffect(() => {
    mainRef.current?.toggleAttribute("inert", open);
    subRef.current?.toggleAttribute("inert", !open);
  }, [open]);
  return (
    // overflow-x: clip hides the sliding panel without making a scroll box;
    // the -10px/10px pair keeps the Paper's padding inside the clip, where
    // the collapsed rail's labels overhang.
    <Box
      sx={{
        flexGrow: 1,
        display: "flex",
        overflowX: "clip",
        mx: "-10px",
        px: "10px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: "200%",
          flexShrink: 0,
          transform: open ? "translateX(-50%)" : "none",
          transition: "transform 240ms cubic-bezier(.3,.7,.2,1)",
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        <Box
          ref={mainRef}
          aria-hidden={open || undefined}
          sx={drillPanelSx(!open)}
        >
          {main}
        </Box>
        <Box
          ref={subRef}
          aria-hidden={!open || undefined}
          sx={drillPanelSx(open)}
        >
          {sub}
        </Box>
      </Box>
    </Box>
  );
};

export default HomeSideBar;
